import type React from "react";
import { useEffect, useState } from "react";

import { useNProgress } from "@tanem/react-nprogress";

export const Progress: React.FC<{
  isAnimating: boolean;
  className?: string;
  barClassName?: string;
}> = ({ isAnimating, className, barClassName }) => {
  const { animationDuration, isFinished, progress } = useNProgress({
    minimum: 0.4,
    animationDuration: 300,
    isAnimating,
  });

  // iOS: stop PAINTING once the exit has had time to play, don't just fade.
  //
  // Reported 2026-08-20 on iPhone: after a soft navigation the bar reached the
  // far end and stayed there permanently. Reaching the END is the tell — while
  // `isAnimating` holds, nprogress trickles asymptotically and never arrives,
  // so a bar at 100% means the navigation had already completed and
  // `isFinished` was true. The exit ran; it just wasn't painted.
  //
  // `Container` hides itself purely by transitioning `opacity` to 0. The glow
  // div below carries `transform: rotate(3deg)` plus a double `box-shadow`,
  // which promotes it to its own compositing layer, and iOS Safari does not
  // reliably repaint a promoted child when only the PARENT's opacity changes.
  // Nothing in the DOM ever unmounted, so there was no second chance.
  //
  // The fade is still the right exit where it works, so keep the transition and
  // stop rendering after it has had `animationDuration` to run. Browsers that
  // honour the transition see no change; iOS gets a hard removal at 300ms
  // instead of a bar that never leaves.
  //
  // Starts unpainted, which is also what the server renders — so an idle page
  // ships no bar markup at all and hydration matches.
  // Keyed on `isAnimating` — the prop — NOT on the hook's `isFinished`.
  // `isFinished` is internal state driven by rAF inside `useNProgress`; it is
  // not observable under happy-dom + fake timers, so a teardown hung off it
  // cannot be regression-tested, and it couples us to the library's state
  // machine. `isAnimating` is ours and is exactly the signal that matters.
  //
  // The grace period is 2x `animationDuration`: once `isAnimating` drops, the
  // hook still runs its completion animation to 100% (one duration), and only
  // then does the container fade (a second duration). Tearing down earlier
  // would clip the exit for browsers that DO paint it.
  const [painted, setPainted] = useState(false);
  useEffect(() => {
    if (isAnimating) {
      setPainted(true);
      return;
    }
    const timer = setTimeout(() => setPainted(false), animationDuration * 2);
    return () => clearTimeout(timer);
  }, [isAnimating, animationDuration]);

  if (!painted) return null;

  return (
    <Container
      animationDuration={animationDuration}
      isFinished={isFinished}
      className={className}
    >
      <Bar
        animationDuration={animationDuration}
        progress={progress}
        className={barClassName}
      />
      {/*
      This example doesn't use a spinner component so the UI stays
      tidy. You're free to render whatever is appropriate for your
      use-case.
      */}
    </Container>
  );
};

const Container = ({
  animationDuration,
  children,
  isFinished,
  className,
}: {
  animationDuration: number;
  children: React.JSX.Element;
  isFinished: boolean;
  className?: string;
}) => (
  <div
    className={className}
    style={{
      opacity: isFinished ? 0 : 1,
      pointerEvents: "none",
      transition: `opacity ${animationDuration}ms linear`,
    }}
  >
    {children}
  </div>
);

const Bar = ({
  animationDuration,
  progress,
  className,
}: {
  animationDuration: number;
  progress: number;
  className?: string;
}) => (
  <div
    className={className}
    style={{
      background: "#29d",
      height: 2,
      left: 0,
      marginLeft: `${(-1 + progress) * 100}%`,
      position: "absolute",
      top: 0,
      transition: `margin-left ${animationDuration}ms linear`,
      width: "100%",
      zIndex: 1031,
    }}
  >
    <div
      style={{
        boxShadow: "0 0 10px #29d, 0 0 5px #29d",
        display: "block",
        height: "100%",
        opacity: 1,
        position: "absolute",
        right: 0,
        transform: "rotate(3deg) translate(0px, -4px)",
        width: 100,
      }}
    />
  </div>
);
